"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from "react";
import {
  addEditableNote,
  createNoteFromRollPosition,
  deleteEditableNote,
  type MidiEditMode,
} from "../lib/midi-editor";
import { isUnresolvedMidiNote, midiToNoteName, type MidiNote } from "../lib/music";
import type { PreviewInstrument } from "../lib/preview-instrument";
import type { VoiceDrawState } from "../lib/voice-draw";
import type { LocalRhythmRefineResult, RhythmRefineStrength } from "../lib/rhythm-refine";
import { AnchoredPopover } from "./AnchoredPopover";
import {
  EDITOR_MAX_MIDI,
  EDITOR_MIN_MIDI,
  EDITOR_ZOOM_LEVELS,
  MAX_EDITOR_ZOOM,
  MIN_EDITOR_ZOOM,
  PITCH_ROW_HEIGHT,
  beatToTimelineX,
  clampEditorZoom,
  editorTimelineWidth,
  horizontalDragToBeatDelta,
  isDesktopPanButton,
  moveMidiByPointer,
  panScrollFromCentroid,
  pitchEdgeAutoPan,
  pointerCentroid,
  scrollLeftForZoomAnchor,
  timelineXToBeat,
  nextEditorZoomLevel,
  zoomFromWheel,
  type EditorGestureState,
  type PointerPoint,
} from "../lib/editor-gesture";

type GestureMode = "move" | "resize-left" | "resize-right";
type PendingZoomAnchor = {
  beat: number;
  viewportX: number;
};
type Gesture = {
  pointerId: number;
  noteIndex: number;
  mode: GestureMode;
  startX: number;
  startY: number;
  startScrollTop: number;
  before: MidiNote[];
  after: MidiNote[];
};

type Props = {
  notes: MidiNote[];
  gridStep: number;
  gridLabel: string;
  timeNudgeGrid: "1/4" | "1/8" | "1/16" | "1/32";
  zoom: number;
  editMode: MidiEditMode;
  previewInstrument: PreviewInstrument;
  selectedNoteIndex: number | null;
  canUndo: boolean;
  canRedo: boolean;
  isPlaying: boolean;
  playheadBeat: number | null;
  voiceDrawState: VoiceDrawState;
  timeSignature: { numerator: number; denominator: number };
  midiImport: { fileName: string; ppq: number; bpm: number; tracks: Array<{ index: number; name: string; noteCount: number }>; selectedTrackIndex: number } | null;
  nextBarNumber: number;
  rhythmRefineSelection: { startBar: number; endBar: number; awaitingEnd: boolean } | null;
  rhythmRefineStrength: RhythmRefineStrength;
  rhythmRefinePreview: LocalRhythmRefineResult | null;
  onSelectRhythmRefineBar: (bar: number) => void;
  onClearRhythmRefineSelection: () => void;
  onEditModeChange: (mode: MidiEditMode) => void;
  onVoiceGestureStart: (pointerId: number, anchorBeat: number, targetNoteIndex: number | null, totalBeats: number) => void;
  onVoiceGestureMove: (pointerId: number, currentBeat: number, totalBeats: number) => void;
  onVoiceGestureEnd: (pointerId: number, currentBeat: number, totalBeats: number) => void;
  onVoiceGestureCancel: (pointerId: number | null, reason: string) => void;
  onPreviewInstrumentChange: (instrument: PreviewInstrument) => void;
  onSelectNote: (index: number | null) => void;
  onPreviewNotes: (notes: MidiNote[]) => void;
  onCommitNotes: (before: MidiNote[], after?: MidiNote[], notice?: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onDelete: () => void;
  onTimeNudge: (direction: -1 | 1) => void;
  onTimeNudgeGridChange: (grid: "1/4" | "1/8" | "1/16" | "1/32") => void;
  onZoomChange: (zoom: number) => void;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onGoToStart: () => void;
  onPreviousBar: () => void;
  onExport: () => void;
  onOpenMidi: () => void;
  onImportedTrackChange: (trackIndex: number) => void;
  onOpenMenu: () => void;
  onAddPass: () => void;
  onRhythmRefineStrengthChange: (strength: RhythmRefineStrength) => void;
  onPreviewRhythmRefine: () => void;
  onApplyRhythmRefine: (apply: boolean) => void;
};


function cloneNotes(notes: MidiNote[]): MidiNote[] {
  return notes.map((note) => ({ ...note }));
}

function snap(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function phaseClass(state: VoiceDrawState): "waiting" | "detecting" | "locked" {
  if (state.phase === "pitch-locked") return "locked";
  if (state.phase === "pitch-searching" && state.candidateMidi !== null) return "detecting";
  return "waiting";
}

export function MobilePianoRollEditor({
  notes,
  gridStep,
  gridLabel,
  timeNudgeGrid,
  zoom,
  editMode,
  previewInstrument,
  selectedNoteIndex,
  canUndo,
  canRedo,
  isPlaying,
  playheadBeat,
  voiceDrawState,
  timeSignature,
  midiImport,
  onEditModeChange,
  onVoiceGestureStart,
  nextBarNumber,
  rhythmRefineSelection,
  rhythmRefineStrength,
  rhythmRefinePreview,
  onSelectRhythmRefineBar,
  onClearRhythmRefineSelection,
  onVoiceGestureMove,
  onVoiceGestureEnd,
  onVoiceGestureCancel,
  onPreviewInstrumentChange,
  onSelectNote,
  onPreviewNotes,
  onCommitNotes,
  onUndo,
  onRedo,
  onDelete,
  onTimeNudge,
  onTimeNudgeGridChange,
  onZoomChange,
  onPlay,
  onPause,
  onStop,
  onGoToStart,
  onPreviousBar,
  onExport,
  onOpenMidi,
  onImportedTrackChange,
  onOpenMenu,
  onAddPass,
  onRhythmRefineStrengthChange,
  onPreviewRhythmRefine,
  onApplyRhythmRefine,
}: Props) {
  const rollRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const barHeaderRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const voicePointerRef = useRef<number | null>(null);
  const activePointersRef = useRef(new Map<number, PointerPoint>());
  const panRef = useRef<{ pointerId: number | null; centroid: PointerPoint } | null>(null);
  const pendingZoomAnchorRef = useRef<PendingZoomAnchor | null>(null);
  const suppressClickRef = useRef(false);
  const capturedPointersRef = useRef(new Map<number, HTMLElement>());
  const initialPitchFitRef = useRef(false);
  const moreTriggerRef = useRef<HTMLButtonElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [gestureState, setGestureState] = useState<EditorGestureState>("IDLE");
  const [capturedPointerLabel, setCapturedPointerLabel] = useState("none");
  const [activePointerCount, setActivePointerCount] = useState(0);
  const [viewportBeat, setViewportBeat] = useState(0);
  const [viewPitch, setViewPitch] = useState("—");
  const [moreOpen, setMoreOpen] = useState(false);
  const beatsPerBar = Math.max(0.25, timeSignature.numerator * 4 / timeSignature.denominator);
  const timelineStartBeat = useMemo(() => {
    const earliest = Math.min(0, ...notes.map((note) => note.startBeat));
    return earliest < 0 ? Math.floor(earliest / beatsPerBar) * beatsPerBar : 0;
  }, [beatsPerBar, notes]);
  const timelineEndBeat = useMemo(
    () => Math.max(beatsPerBar, Math.ceil(Math.max(beatsPerBar, ...notes.map((note) => note.startBeat + note.durationBeats)) / beatsPerBar) * beatsPerBar),
    [beatsPerBar, notes],
  );
  const totalBeats = timelineEndBeat - timelineStartBeat;
  const pitchBounds = { min: EDITOR_MIN_MIDI, max: EDITOR_MAX_MIDI, span: EDITOR_MAX_MIDI - EDITOR_MIN_MIDI + 1 };
  const initialPitchCenter = useMemo(() => {
    if (!notes.length) return 66;
    const low = Math.min(...notes.map((note) => note.midi));
    const high = Math.max(...notes.map((note) => note.midi));
    return (low + high) / 2;
  }, [notes]);
  const barCount = Math.max(1, Math.round(timelineEndBeat / beatsPerBar));
  const contentWidth = Math.max(editorTimelineWidth(totalBeats, zoom), barCount * 88 + (timelineStartBeat < 0 ? 88 : 0));
  const contentHeight = pitchBounds.span * PITCH_ROW_HEIGHT;
  const hasPickup = timelineStartBeat < 0;
  const voiceTarget = voiceDrawState.targetNoteIndex === null ? null : notes[voiceDrawState.targetNoteIndex] ?? null;
  const voiceRange = voiceTarget
    ? { startBeat: voiceTarget.startBeat, durationBeats: voiceTarget.durationBeats, gridCount: Math.max(1, Math.round(voiceTarget.durationBeats / gridStep)) }
    : voiceDrawState.range;
  const ghostMidi = voiceDrawState.lockedMidi ?? voiceDrawState.candidateMidi ?? voiceTarget?.midi ?? Math.round((pitchBounds.min + pitchBounds.max) / 2);
  const showVoiceGhost = editMode === "voice" && voiceDrawState.pointerId !== null && voiceRange !== null;
  const unresolvedCount = notes.filter(isUnresolvedMidiNote).length;
  const visibleBeat = playheadBeat ?? viewportBeat;
  const currentBar = Math.max(0, Math.min(barCount - 1, Math.floor(Math.max(0, visibleBeat) / beatsPerBar)));
  const visibleBarLabel = visibleBeat < 0
    ? "PICKUP · BEAT " + (((visibleBeat % beatsPerBar) + beatsPerBar) % beatsPerBar + 1).toFixed(2)
    : "BAR " + (Math.floor(visibleBeat / beatsPerBar) + 1) + " · BEAT " + (visibleBeat % beatsPerBar + 1).toFixed(2);

  function timelineXForBeat(beat: number, width = contentWidth): number {
    return beatToTimelineX(beat - timelineStartBeat, totalBeats, width);
  }

  function beatForTimelineX(x: number, width = contentWidth): number {
    return timelineStartBeat + timelineXToBeat(x, totalBeats, width);
  }

  function updateViewportDebug(viewport: HTMLDivElement) {
    const high = Math.max(EDITOR_MIN_MIDI, Math.min(EDITOR_MAX_MIDI, EDITOR_MAX_MIDI - Math.floor(viewport.scrollTop / PITCH_ROW_HEIGHT)));
    const low = Math.max(EDITOR_MIN_MIDI, Math.min(EDITOR_MAX_MIDI, EDITOR_MAX_MIDI - Math.ceil((viewport.scrollTop + viewport.clientHeight) / PITCH_ROW_HEIGHT) + 1));
    setViewPitch(midiToNoteName(low) + "–" + midiToNoteName(high) + " (" + low + "–" + high + ")");
  }

  function updateViewportTimeline(viewport: HTMLDivElement) {
    setViewportBeat(beatForTimelineX(viewport.scrollLeft));
    if (barHeaderRef.current && Math.abs(barHeaderRef.current.scrollLeft - viewport.scrollLeft) > 1) barHeaderRef.current.scrollLeft = viewport.scrollLeft;
    updateViewportDebug(viewport);
  }

  function updateTimelineFromBarHeader(header: HTMLDivElement) {
    const viewport = viewportRef.current;
    if (!viewport || Math.abs(viewport.scrollLeft - header.scrollLeft) <= 1) return;
    viewport.scrollLeft = header.scrollLeft;
    setViewportBeat(beatForTimelineX(viewport.scrollLeft));
  }

  function requestHorizontalZoom(nextZoom: number, viewportX?: number, anchorBeat?: number) {
    const viewport = viewportRef.current;
    if (!viewport) {
      onZoomChange(clampEditorZoom(nextZoom));
      return;
    }
    const safeViewportX = Math.max(0, Math.min(viewport.clientWidth, viewportX ?? viewport.clientWidth / 2));
    pendingZoomAnchorRef.current = {
      beat: anchorBeat ?? beatForTimelineX(viewport.scrollLeft + safeViewportX),
      viewportX: safeViewportX,
    };
    onZoomChange(clampEditorZoom(nextZoom));
  }

  function handleViewportWheel(event: ReactWheelEvent<HTMLDivElement>) {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    requestHorizontalZoom(zoomFromWheel(zoom, event.deltaY), event.clientX - bounds.left);
  }
  function capturePointer(element: HTMLElement, pointerId: number) {
    try {
      element.setPointerCapture(pointerId);
      capturedPointersRef.current.set(pointerId, element);
      setCapturedPointerLabel(String(pointerId));
    } catch {
      capturedPointersRef.current.delete(pointerId);
      setCapturedPointerLabel("none");
    }
  }

  function releasePointer(pointerId: number) {
    const element = capturedPointersRef.current.get(pointerId);
    if (element) {
      try {
        if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
      } catch {
        // The browser can release capture before pointercancel reaches React.
      }
    }
    capturedPointersRef.current.delete(pointerId);
    setCapturedPointerLabel(capturedPointersRef.current.size ? [...capturedPointersRef.current.keys()].join(",") : "none");
  }

  function resetTransientGesture(reason: string) {
    const gesture = gestureRef.current;
    if (gesture) onPreviewNotes(gesture.before);
    gestureRef.current = null;
    const voicePointer = voicePointerRef.current;
    voicePointerRef.current = null;
    if (voicePointer !== null) onVoiceGestureCancel(voicePointer, reason);
    [...capturedPointersRef.current.keys()].forEach(releasePointer);
    activePointersRef.current.clear();
    setActivePointerCount(0);
    panRef.current = null;
    pendingZoomAnchorRef.current = null;
    suppressClickRef.current = false;
    setIsPanning(false);
    setGestureState("IDLE");
  }

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || initialPitchFitRef.current) return;
    initialPitchFitRef.current = true;
    const targetTop = (EDITOR_MAX_MIDI - initialPitchCenter + 0.5) * PITCH_ROW_HEIGHT - viewport.clientHeight / 2;
    viewport.scrollTop = Math.max(0, Math.min(contentHeight - viewport.clientHeight, targetTop));
    updateViewportDebug(viewport);
  }, [contentHeight, initialPitchCenter]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const pending = pendingZoomAnchorRef.current;
    if (!viewport || !pending) return;
    viewport.scrollLeft = scrollLeftForZoomAnchor({
      anchorBeat: pending.beat - timelineStartBeat,
      viewportX: pending.viewportX,
      totalBeats,
      contentWidth,
      viewportWidth: viewport.clientWidth,
    });
    pendingZoomAnchorRef.current = null;
    setViewportBeat(timelineStartBeat + timelineXToBeat(viewport.scrollLeft, totalBeats, contentWidth));
  }, [contentWidth, timelineStartBeat, totalBeats]);
useEffect(() => {
    const cancelForLayoutChange = () => resetTransientGesture("画面状態が変わったため操作をキャンセルしました");
    const cancelForBackground = () => {
      if (document.visibilityState === "hidden") cancelForLayoutChange();
    };
    window.addEventListener("orientationchange", cancelForLayoutChange);
    document.addEventListener("visibilitychange", cancelForBackground);
    return () => {
      window.removeEventListener("orientationchange", cancelForLayoutChange);
      document.removeEventListener("visibilitychange", cancelForBackground);
      resetTransientGesture("編集画面を終了したため操作をキャンセルしました");
    };
    // Gesture callbacks are intentionally captured for this mounted editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || playheadBeat === null) return;
    const playheadX = beatToTimelineX(playheadBeat - timelineStartBeat, totalBeats, contentWidth);
    const margin = Math.min(72, viewport.clientWidth * 0.18);
    if (playheadX < viewport.scrollLeft + margin || playheadX > viewport.scrollLeft + viewport.clientWidth - margin) {
      viewport.scrollTo({ left: Math.max(0, playheadX - margin), behavior: isPlaying ? "auto" : "smooth" });
    }
  }, [contentWidth, isPlaying, playheadBeat, timelineStartBeat, totalBeats]);

function beatAtClientX(clientX: number): number {
    const roll = rollRef.current;
    if (!roll) return 0;
    const bounds = roll.getBoundingClientRect();
    return beatForTimelineX(clientX - bounds.left, bounds.width);
  }

  function beginVoicePointer(event: ReactPointerEvent<HTMLElement>, targetNoteIndex: number | null) {
    if (editMode !== "voice" || voiceDrawState.phase !== "ready" || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const beat = beatAtClientX(event.clientX);
    capturePointer(event.currentTarget, event.pointerId);
    voicePointerRef.current = event.pointerId;
    setGestureState(targetNoteIndex === null ? "VOICE_DRAW" : "VOICE_REPITCH");
    if (targetNoteIndex !== null) onSelectNote(targetNoteIndex);
    else onSelectNote(null);
    onVoiceGestureStart(event.pointerId, beat, targetNoteIndex, totalBeats);
  }

  function moveVoicePointer(event: ReactPointerEvent<HTMLElement>) {
    if (editMode !== "voice" || voicePointerRef.current !== event.pointerId) return;
    event.preventDefault();
    onVoiceGestureMove(event.pointerId, beatAtClientX(event.clientX), totalBeats);
  }

  function endVoicePointer(event: ReactPointerEvent<HTMLElement>) {
    if (editMode !== "voice" || voicePointerRef.current !== event.pointerId) return;
    event.preventDefault();
    const pointerId = event.pointerId;
    const beat = beatAtClientX(event.clientX);
    voicePointerRef.current = null;
    releasePointer(pointerId);
    setGestureState("IDLE");
    onVoiceGestureEnd(pointerId, beat, totalBeats);
  }

  function cancelVoicePointer(event: ReactPointerEvent<HTMLElement>) {
    if (voicePointerRef.current !== event.pointerId) return;
    event.preventDefault();
    voicePointerRef.current = null;
    releasePointer(event.pointerId);
    setGestureState("IDLE");
    onVoiceGestureCancel(event.pointerId, "タッチ操作が中断されたため変更しませんでした");
  }

  function cancelEditForPan() {
    if (voicePointerRef.current !== null) {
      const pointerId = voicePointerRef.current;
      voicePointerRef.current = null;
      releasePointer(pointerId);
      onVoiceGestureCancel(pointerId, "2本指PANへ切り替えたためVOICE入力は反映しませんでした");
    }
    if (gestureRef.current) {
      const gesture = gestureRef.current;
      onPreviewNotes(gesture.before);
      gestureRef.current = null;
      releasePointer(gesture.pointerId);
    }
    setGestureState("PAN");
  }

  function beginViewportPointer(event: ReactPointerEvent<HTMLDivElement>) {
    const viewport = viewportRef.current;
    if (!viewport) return;
    if (isDesktopPanButton(event.pointerType, event.button)) {
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = true;
      panRef.current = { pointerId: event.pointerId, centroid: { x: event.clientX, y: event.clientY } };
      capturePointer(viewport, event.pointerId);
      setGestureState("PAN");
      setIsPanning(true);
      return;
    }
    if (event.pointerType !== "touch") return;
    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    setActivePointerCount(activePointersRef.current.size);
    if (activePointersRef.current.size < 2) return;
    event.preventDefault();
    event.stopPropagation();
    cancelEditForPan();
    suppressClickRef.current = true;
    activePointersRef.current.forEach((_, pointerId) => capturePointer(viewport, pointerId));
    const centroid = pointerCentroid(activePointersRef.current.values());
    if (centroid) panRef.current = { pointerId: null, centroid };
    setIsPanning(true);
  }

  function moveViewportPointer(event: ReactPointerEvent<HTMLDivElement>) {
    const viewport = viewportRef.current;
    if (!viewport) return;
    if (event.pointerType === "touch" && activePointersRef.current.has(event.pointerId)) {
      activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      setActivePointerCount(activePointersRef.current.size);
    }
    const pan = panRef.current;
    const mousePan = pan?.pointerId === event.pointerId;
    const touchPan = pan?.pointerId === null && activePointersRef.current.size >= 2;
    if (!pan || (!mousePan && !touchPan)) {
      if (suppressClickRef.current) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const centroid = mousePan ? { x: event.clientX, y: event.clientY } : pointerCentroid(activePointersRef.current.values());
    if (!centroid) return;
    const next = panScrollFromCentroid({ left: viewport.scrollLeft, top: viewport.scrollTop }, pan.centroid, centroid);
    viewport.scrollLeft = next.left;
    viewport.scrollTop = next.top;
    setGestureState("PAN");
    updateViewportTimeline(viewport);
    pan.centroid = centroid;
  }

  function endViewportPointer(event: ReactPointerEvent<HTMLDivElement>) {
    const wasPan = panRef.current !== null || suppressClickRef.current;
    activePointersRef.current.delete(event.pointerId);
    setActivePointerCount(activePointersRef.current.size);
    releasePointer(event.pointerId);
    if (!wasPan) return;
    event.preventDefault();
    event.stopPropagation();
    if (panRef.current?.pointerId === event.pointerId || activePointersRef.current.size < 2) {
      panRef.current = null;
      setIsPanning(false);
      setGestureState("IDLE");
    }
    if (activePointersRef.current.size === 0) {
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    }
  }

  function suppressPanClick(event: ReactMouseEvent<HTMLDivElement>) {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  }

  function beginNoteGesture(event: ReactPointerEvent<HTMLElement>, noteIndex: number, mode: GestureMode) {
    if (editMode !== "select" || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const viewport = viewportRef.current;
    onSelectNote(noteIndex);
    capturePointer(event.currentTarget, event.pointerId);
    gestureRef.current = {
      pointerId: event.pointerId,
      noteIndex,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startScrollTop: viewport?.scrollTop ?? 0,
      before: cloneNotes(notes),
      after: cloneNotes(notes),
    };
    setGestureState(mode === "move" ? "MOVE" : mode === "resize-left" ? "RESIZE_START" : "RESIZE_END");
  }

  function moveGesture(event: ReactPointerEvent<HTMLElement>) {
    const gesture = gestureRef.current;
    const roll = rollRef.current;
    const viewport = viewportRef.current;
    if (editMode !== "select" || !gesture || gesture.pointerId !== event.pointerId || !roll || !viewport) return;
    event.preventDefault();
    const original = gesture.before[gesture.noteIndex];
    if (!original) return;
    const viewportBounds = viewport.getBoundingClientRect();
    const autoPan = pitchEdgeAutoPan(event.clientY, viewportBounds.top, viewportBounds.bottom);
    if (autoPan !== 0) {
      viewport.scrollTop = Math.max(0, Math.min(contentHeight - viewport.clientHeight, viewport.scrollTop + autoPan));
      updateViewportDebug(viewport);
    }
    const bounds = roll.getBoundingClientRect();
    const deltaBeat = horizontalDragToBeatDelta(event.clientX - gesture.startX, totalBeats, bounds.width);
    const next = cloneNotes(gesture.before);

    if (gesture.mode === "move") {
      next[gesture.noteIndex] = {
        ...original,
        midi: moveMidiByPointer({
          originalMidi: original.midi,
          startClientY: gesture.startY,
          currentClientY: event.clientY,
          startScrollTop: gesture.startScrollTop,
          currentScrollTop: viewport.scrollTop,
        }),
        startBeat: Math.max(timelineStartBeat, snap(original.startBeat + deltaBeat, gridStep)),
      };
    } else if (gesture.mode === "resize-left") {
      const endBeat = original.startBeat + original.durationBeats;
      const startBeat = Math.max(timelineStartBeat, Math.min(endBeat - gridStep, snap(original.startBeat + deltaBeat, gridStep)));
      next[gesture.noteIndex] = { ...original, startBeat, durationBeats: Math.max(gridStep, endBeat - startBeat) };
    } else {
      const endBeat = Math.max(original.startBeat + gridStep, snap(original.startBeat + original.durationBeats + deltaBeat, gridStep));
      next[gesture.noteIndex] = { ...original, durationBeats: endBeat - original.startBeat };
    }
    gesture.after = next;
    onPreviewNotes(next);
  }

  function finishGesture(pointerId: number, commit: boolean) {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== pointerId) return;
    gestureRef.current = null;
    releasePointer(pointerId);
    setGestureState("IDLE");
    if (commit) onCommitNotes(gesture.before, gesture.after);
    else onPreviewNotes(gesture.before);
  }

  function endGesture(event: ReactPointerEvent<HTMLElement>) {
    event.preventDefault();
    finishGesture(event.pointerId, true);
  }

  function cancelGesture(event: ReactPointerEvent<HTMLElement>) {
    event.preventDefault();
    finishGesture(event.pointerId, false);
  }
  function commitDrawNote(added: MidiNote) {
    const before = cloneNotes(notes);
    const next = addEditableNote(notes, [added]);
    onSelectNote(next.findIndex((note) => note.startBeat === added.startBeat && note.midi === added.midi));
    onCommitNotes(before, next, midiToNoteName(added.midi) + " を追加しました。");
  }

  function addDrawNote(event: ReactMouseEvent<HTMLDivElement>) {
    if (editMode !== "draw" || event.target !== event.currentTarget) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const added = createNoteFromRollPosition({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
      width: bounds.width,
      height: bounds.height,
      totalBeats,
      gridStep,
      minimumMidi: pitchBounds.min,
      maximumMidi: pitchBounds.max,
    });
    commitDrawNote({ ...added, startBeat: added.startBeat + timelineStartBeat });
  }

  function commitDeleteDrawNote(noteIndex: number) {
    const before = cloneNotes(notes);
    const deleted = notes[noteIndex];
    if (!deleted) return;
    onSelectNote(null);
    onCommitNotes(before, deleteEditableNote(notes, noteIndex), midiToNoteName(deleted.midi) + " を削除しました。");
  }

  function deleteDrawNote(event: ReactMouseEvent<HTMLDivElement>, noteIndex: number) {
    if (editMode !== "draw") return;
    event.preventDefault();
    event.stopPropagation();
    commitDeleteDrawNote(noteIndex);
  }

  function handleRollKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (editMode !== "draw" || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    const roll = rollRef.current;
    if (!roll) return;
    const bounds = roll.getBoundingClientRect();
    const added = createNoteFromRollPosition({
      x: ((playheadBeat ?? timelineStartBeat) - timelineStartBeat) / totalBeats * bounds.width,
      y: bounds.height / 2,
      width: bounds.width,
      height: bounds.height,
      totalBeats,
      gridStep,
      minimumMidi: pitchBounds.min,
      maximumMidi: pitchBounds.max,
    });
    commitDrawNote({ ...added, startBeat: added.startBeat + timelineStartBeat });
  }

  const voiceBeatLabel = voiceRange
    ? `BAR ${Math.floor(voiceRange.startBeat / beatsPerBar) + 1} · BEAT ${(voiceRange.startBeat % beatsPerBar + 1).toFixed(2)}`
    : "指で時間範囲を指定";

  return (
    <section className={`mobile-midi-editor edit-mode-${editMode}`} aria-label="タッチ操作MIDI編集">
      <div className="mobile-editor-toolbar">
        <div className="editor-title"><span>EDIT · PIANO ROLL</span><strong>{notes.length} NOTES{unresolvedCount ? ` · ? ${unresolvedCount}` : ""} · {visibleBarLabel}</strong></div>
        <div className="editor-mode-toggle" aria-label="編集モード">
          <button className={editMode === "select" ? "active" : ""} aria-pressed={editMode === "select"} onClick={() => { resetTransientGesture("SELECTへ切り替えました"); onEditModeChange("select"); }}>↖<small>SELECT</small></button>
          <button className={editMode === "draw" ? "active" : ""} aria-pressed={editMode === "draw"} onClick={() => { resetTransientGesture("DRAWへ切り替えました"); onEditModeChange("draw"); }}>✎<small>DRAW</small></button>
        </div>
        <div className="editor-tool-group">
          <button onClick={onUndo} disabled={!canUndo} aria-label="元に戻す">↶<small>UNDO</small></button>
        </div>
        <div className="editor-tool-group editor-transport-group">
          <button onClick={isPlaying ? onPause : onPlay} disabled={!notes.length}>{isPlaying ? "Ⅱ" : "▶"}<small>{isPlaying ? "PAUSE" : "PLAY"}</small></button>
          <button onClick={onStop} disabled={!isPlaying} aria-label="現在位置で停止">■<small>STOP</small></button>
        </div>
        <button ref={moreTriggerRef} type="button" className="editor-more-trigger" aria-expanded={moreOpen} aria-controls="editor-more-panel" onClick={() => setMoreOpen((current) => !current)}>•••<small>MORE</small></button>
        <AnchoredPopover id="editor-more-panel" open={moreOpen} triggerRef={moreTriggerRef} onClose={() => setMoreOpen(false)} className="editor-more-panel">
            <button onClick={() => { setMoreOpen(false); onOpenMenu(); }}>☰<small>MENU</small></button>
            <button className={editMode === "voice" ? "active voice" : "voice"} aria-pressed={editMode === "voice"} onClick={() => { resetTransientGesture("VOICEへ切り替えました"); onEditModeChange("voice"); setMoreOpen(false); }}>●<small>VOICE</small></button>
            <button onClick={() => { onRedo(); setMoreOpen(false); }} disabled={!canRedo}>↷<small>REDO</small></button>
            <button onClick={() => { viewportRef.current?.scrollTo({ left: 0, behavior: "smooth" }); onGoToStart(); setMoreOpen(false); }}>|◀<small>START</small></button>
            <button onClick={() => { onPreviousBar(); setMoreOpen(false); }}>−1<small>BAR</small></button>
            <div className="editor-zoom" aria-label="横方向ズーム"><button onClick={() => requestHorizontalZoom(nextEditorZoomLevel(zoom, -1))} disabled={zoom <= MIN_EDITOR_ZOOM}>−</button><details><summary>{zoom.toFixed(1)}×</summary><div>{EDITOR_ZOOM_LEVELS.map((level) => <button key={level} className={Math.abs(zoom - level) < 0.001 ? "active" : ""} onClick={() => requestHorizontalZoom(level)}>{level.toFixed(1)}×</button>)}</div></details><button onClick={() => requestHorizontalZoom(nextEditorZoomLevel(zoom, 1))} disabled={zoom >= MAX_EDITOR_ZOOM}>＋</button></div>
            <button className={previewInstrument === "piano" ? "active" : ""} onClick={() => { onPreviewInstrumentChange("piano"); setMoreOpen(false); }}>♩<small>PIANO</small></button>
            <button className={previewInstrument === "hold" ? "active" : ""} onClick={() => { onPreviewInstrumentChange("hold"); setMoreOpen(false); }}>━<small>HOLD</small></button>
            <button onClick={() => { onDelete(); setMoreOpen(false); }} disabled={editMode !== "select" || selectedNoteIndex === null} className="editor-delete">×<small>DELETE</small></button>
            <button onClick={() => { onOpenMidi(); setMoreOpen(false); }} className="editor-open-midi">⇧<small>OPEN MIDI</small></button>
            <button onClick={() => { onExport(); setMoreOpen(false); }} disabled={!notes.length} className="editor-export">⇩<small>MIDI</small></button>
            <button onClick={() => { onAddPass(); setMoreOpen(false); }} className="editor-add-pass">＋<small>続きを追加 · BAR {nextBarNumber}</small></button>
            <label><span>CAPTURE GRID</span><strong>{gridLabel}</strong></label>
        </AnchoredPopover>
      </div>

      {process.env.NODE_ENV !== "production" && (
        <output className="editor-gesture-debug" aria-label="Gesture debug">
          MODE: {editMode.toUpperCase()} · SELECTED: {selectedNoteIndex === null ? "none" : "note-" + selectedNoteIndex} · GESTURE: {gestureState} · POINTERS: {activePointerCount} · CAPTURED: {capturedPointerLabel} · PAN: {isPanning ? "true" : "false"} · VIEW PITCH: {viewPitch}
        </output>
      )}
      <section className="mobile-bar-selector" aria-label="録音・修正する小節範囲">
        <header><div><span>BAR SELECT</span><strong>{rhythmRefineSelection ? `BAR ${rhythmRefineSelection.startBar + 1}${rhythmRefineSelection.endBar > rhythmRefineSelection.startBar ? `–${rhythmRefineSelection.endBar + 1}` : ""}` : "未選択"}</strong><small>{rhythmRefineSelection?.awaitingEnd ? "終了BARをタップ（前方向・後方向どちらも可）" : "1回目=開始 · 2回目=終了"}</small></div>{rhythmRefineSelection && <button type="button" onClick={onClearRhythmRefineSelection}>選択解除</button>}</header>
        <div ref={barHeaderRef} className="mobile-bar-header-scroll" onScroll={(event) => updateTimelineFromBarHeader(event.currentTarget)}>
          <div className="mobile-bar-header-track" style={{ width: `${contentWidth}px` }}>
            {hasPickup && <span className="pickup-label" style={{ left: 0, width: timelineXForBeat(0) }}><b>PICKUP</b></span>}
            {Array.from({ length: barCount }, (_, bar) => {
              const selected = Boolean(rhythmRefineSelection && bar >= rhythmRefineSelection.startBar && bar <= rhythmRefineSelection.endBar);
              return <button type="button" key={bar} className={`${selected ? "selected" : ""} ${bar === currentBar ? "current" : ""}`} aria-pressed={selected} style={{ left: timelineXForBeat(bar * beatsPerBar) + "px", width: beatToTimelineX(beatsPerBar, totalBeats, contentWidth) + "px" }} onClick={() => onSelectRhythmRefineBar(bar)}><b>{selected ? "✓ " : ""}BAR {bar + 1}</b>{bar === currentBar && <small>現在</small>}</button>;
            })}
          </div>
        </div>
      </section>
      <div
        ref={viewportRef}
        className={`mobile-roll-viewport ${isPanning ? "is-panning" : ""}`}
        onScroll={(event) => updateViewportTimeline(event.currentTarget)}
        onWheel={handleViewportWheel}
        onPointerDownCapture={beginViewportPointer}
        onPointerMoveCapture={moveViewportPointer}
        onPointerUpCapture={endViewportPointer}
        onPointerCancelCapture={endViewportPointer}
        onClickCapture={suppressPanClick}
        onContextMenu={(event) => event.preventDefault()}
      >
        <div
          ref={rollRef}
          className="mobile-roll-content"
          style={{ width: `${contentWidth}px`, height: `${contentHeight}px`, "--grid-columns": totalBeats / gridStep, "--beat-columns": totalBeats } as CSSProperties}
          role="listbox"
          tabIndex={0}
          aria-label={editMode === "draw" ? "MIDIノート描画エリア。EnterまたはSpaceで再生位置へ追加" : editMode === "voice" ? "VOICE DRAWエリア。指で押している間だけ声のPitchを読み取る" : "MIDIノート選択・調整エリア"}
          onClick={addDrawNote}
          onKeyDown={handleRollKeyDown}
          onPointerDown={(event) => {
            if (editMode === "voice") beginVoicePointer(event, null);
            else if (editMode === "select" && event.target === event.currentTarget) onSelectNote(null);
          }}
          onPointerMove={moveVoicePointer}
          onPointerUp={endVoicePointer}
          onPointerCancel={cancelVoicePointer}
        >
          {hasPickup && <i className="mobile-bar-shade pickup" style={{ left: 0, width: timelineXForBeat(0) }} />}
          {Array.from({ length: barCount }, (_, bar) => (
            <i key={`shade-${bar}`} className={`mobile-bar-shade ${bar % 2 ? "alternate" : ""}`} style={{ left: `${timelineXForBeat(bar * beatsPerBar)}px`, width: `${beatToTimelineX(beatsPerBar, totalBeats, contentWidth)}px` }} />
          ))}
          {hasPickup && <i className="mobile-bar-boundary pickup-start" style={{ left: 0 }} />}
          {Array.from({ length: barCount + 1 }, (_, bar) => (
            <i key={`boundary-${bar}`} className="mobile-bar-boundary" style={{ left: `${timelineXForBeat(bar * beatsPerBar)}px` }} />
          ))}
          {Array.from({ length: pitchBounds.span }, (_, index) => pitchBounds.max - index).map((midi) => (
            <i
              key={midi}
              className={`mobile-pitch-row ${[1, 3, 6, 8, 10].includes(midi % 12) ? "black-row" : ""}`}
              style={{ top: `${((pitchBounds.max - midi) / pitchBounds.span) * 100}%`, height: `${100 / pitchBounds.span}%` }}
            />
          ))}
          <div className="mobile-pitch-ruler" aria-hidden="true">
            {Array.from({ length: pitchBounds.span }, (_, index) => pitchBounds.max - index).filter((midi) => midi % 12 === 0).map((midi) => (
              <span key={midi} style={{ top: `${((pitchBounds.max - midi + 0.5) / pitchBounds.span) * 100}%` }}>{midiToNoteName(midi)}</span>
            ))}
          </div>
          {playheadBeat !== null && <i className="mobile-playhead" style={{ left: `${timelineXForBeat(playheadBeat)}px` }} aria-hidden="true" />}
          {rhythmRefinePreview?.previewNotes.map((note, index) => (
            <div
              key={"refined-ghost-" + index}
              className="mobile-note rhythm-refine-ghost"
              style={{
                left: timelineXForBeat(note.startBeat) + "px",
                width: beatToTimelineX(note.durationBeats, totalBeats, contentWidth) + "px",
                top: ((pitchBounds.max - note.midi) / pitchBounds.span) * 100 + "%",
                height: 100 / pitchBounds.span + "%",
              }}
              aria-hidden="true"
            />
          ))}
          {notes.map((note, index) => {
            const selected = index === selectedNoteIndex;
            const unresolved = isUnresolvedMidiNote(note);
            const repitchSource = editMode === "voice" && voiceDrawState.pointerId !== null && voiceDrawState.targetNoteIndex === index;
            return (
              <div
                key={index}
                className={`mobile-note ${selected ? "selected" : ""} ${unresolved ? "unresolved" : ""} ${repitchSource ? "voice-repitch-source" : ""}`}
                style={{
                  left: `${timelineXForBeat(note.startBeat)}px`,
                  width: `${beatToTimelineX(note.durationBeats, totalBeats, contentWidth)}px`,
                  top: `${((pitchBounds.max - note.midi) / pitchBounds.span) * 100}%`,
                  height: `${100 / pitchBounds.span}%`,
                }}
                role="option"
                tabIndex={0}
                aria-selected={selected}
                aria-label={`${unresolved ? "音程未確定" : midiToNoteName(note.midi)}、${note.startBeat.toFixed(2)}拍、長さ${note.durationBeats.toFixed(2)}拍`}
                onClick={(event) => deleteDrawNote(event, index)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  event.stopPropagation();
                  if (editMode === "draw") commitDeleteDrawNote(index);
                  else if (editMode === "select") onSelectNote(index);
                }}
                onPointerDown={(event) => editMode === "voice" ? beginVoicePointer(event, index) : (!selected && beginNoteGesture(event, index, "move"))}
                onPointerMove={(event) => editMode === "voice" ? moveVoicePointer(event) : (!selected && moveGesture(event))}
                onPointerUp={(event) => editMode === "voice" ? endVoicePointer(event) : (!selected && endGesture(event))}
                onPointerCancel={(event) => editMode === "voice" ? cancelVoicePointer(event) : (!selected && cancelGesture(event))}
              >
                {unresolved && !(editMode === "select" && selected) && <span className="unresolved-label">?</span>}
                {editMode === "select" && selected && <>
                  <b className="resize-handle left" aria-label="ノート開始位置を変更" onPointerDown={(event) => beginNoteGesture(event, index, "resize-left")} onPointerMove={moveGesture} onPointerUp={endGesture} onPointerCancel={cancelGesture} />
                  <span className="note-move-body" onPointerDown={(event) => beginNoteGesture(event, index, "move")} onPointerMove={moveGesture} onPointerUp={endGesture} onPointerCancel={cancelGesture}>{unresolved ? "?" : midiToNoteName(note.midi)}</span>
                  <b className="resize-handle right" aria-label="ノート終了位置を変更" onPointerDown={(event) => beginNoteGesture(event, index, "resize-right")} onPointerMove={moveGesture} onPointerUp={endGesture} onPointerCancel={cancelGesture} />
                </>}
              </div>
            );
          })}
          {showVoiceGhost && voiceRange && (
            <div
              className={`mobile-note voice-draw-ghost ${phaseClass(voiceDrawState)}`}
              style={{
                left: `${timelineXForBeat(voiceRange.startBeat)}px`,
                width: `${beatToTimelineX(voiceRange.durationBeats, totalBeats, contentWidth)}px`,
                top: `${((pitchBounds.max - ghostMidi) / pitchBounds.span) * 100}%`,
                height: `${100 / pitchBounds.span}%`,
              }}
              aria-live="polite"
            >
              <span>{voiceDrawState.lockedMidi === null ? (voiceDrawState.candidateMidi === null ? "PITCH…" : midiToNoteName(voiceDrawState.candidateMidi)) : `${midiToNoteName(voiceDrawState.lockedMidi)} LOCK`}</span>
              <small>{voiceRange.durationBeats.toFixed(2)}拍 · {voiceRange.gridCount} GRID</small>
            </div>
          )}
          {!notes.length && !showVoiceGhost && <p className="mobile-editor-empty">{editMode === "draw" ? "空白をタップすると、GRID長のMIDIノートを追加できます。" : editMode === "voice" ? "空白を左右へ押したまま動かし、その間に1音を歌ってください。" : "DRAWまたはVOICEへ切り替えると、録音なしでもノートを追加できます。"}</p>}
        </div>
      </div>
      {midiImport && (
        <label className="midi-import-strip">
          <span>OPENED · {midiImport.fileName} · PPQ {midiImport.ppq} · {midiImport.bpm} BPM · {timeSignature.numerator}/{timeSignature.denominator}</span>
          <select value={midiImport.selectedTrackIndex} onChange={(event) => onImportedTrackChange(Number(event.target.value))} aria-label="編集するMIDIトラック">
            {midiImport.tracks.map((track) => <option key={track.index} value={track.index}>{track.name} · {track.noteCount} notes</option>)}
          </select>
        </label>
      )}
      <details className="rhythm-refine-drawer">
        <summary>選択範囲のリズム補正</summary>
        <div className="rhythm-refine-strip">
        <div className="local-refine-controls">
          <div><span>LOCAL RHYTHM REFINE</span><strong>{rhythmRefineSelection ? `BAR ${rhythmRefineSelection.startBar + 1}${rhythmRefineSelection.endBar > rhythmRefineSelection.startBar ? `–${rhythmRefineSelection.endBar + 1}` : ""}` : "上のBARをタップ"}</strong></div>
          {rhythmRefineSelection && <>
            <div className="refine-segments" aria-label="補正強度">
              {(["SOFT", "NORMAL", "TIGHT"] as RhythmRefineStrength[]).map((strength) => <button key={strength} className={rhythmRefineStrength === strength ? "active" : ""} onClick={() => onRhythmRefineStrengthChange(strength)}>{strength}</button>)}
            </div>
            <button className="refine-preview-action" onClick={onPreviewRhythmRefine}>リズムを整える</button>
          </>}
        </div>
        {rhythmRefinePreview && <div className={"rhythm-refine-result " + (rhythmRefinePreview.shouldRefine ? "candidate" : "low-confidence")}>
          <div><span>RAW</span><b>現在の緑note</b></div><div><span>REFINED</span><b>水色outline</b></div>
          <strong>{rhythmRefinePreview.candidate ? rhythmRefinePreview.candidate.gridLabel + " パターンらしさ " + Math.round(rhythmRefinePreview.candidate.confidence * 100) + "%" : "候補なし"}</strong>
          <small>{rhythmRefinePreview.message} · onset {rhythmRefinePreview.onsetAdjustments} / duration {rhythmRefinePreview.durationAdjustments}</small>
          <div><button onClick={() => onApplyRhythmRefine(false)}>CANCEL</button><button className="apply-refine" onClick={() => onApplyRhythmRefine(true)} disabled={!rhythmRefinePreview.shouldRefine || !rhythmRefinePreview.hasChanges}>APPLY</button></div>
        </div>}
        </div>
      </details>
      <div className={`selected-note-strip ${editMode === "voice" ? `voice-status ${phaseClass(voiceDrawState)}` : ""}`}>
        {editMode === "select" && (
          <div className="time-nudge-controls" aria-label={`TIME NUDGE ${timeNudgeGrid}`}>
            <span>TIME NUDGE</span>
            <div className="time-nudge-grid" aria-label="TIME NUDGE単位">
              {(["1/32", "1/16", "1/8", "1/4"] as const).map((value) => <button type="button" key={value} className={timeNudgeGrid === value ? "active" : ""} onClick={() => onTimeNudgeGridChange(value)}>{value}</button>)}
            </div>
            <button type="button" onClick={() => onTimeNudge(-1)} aria-label="選択範囲を1 GRID前へ">←</button>
            <button type="button" onClick={() => onTimeNudge(1)} aria-label="選択範囲を1 GRID後ろへ">→</button>
          </div>
        )}
        {editMode === "draw"
          ? <><strong>DRAW</strong><span>1本指=追加/削除 · 2本指=PAN</span><small>移動・長さ変更はSELECT</small></>
          : editMode === "voice"
            ? <><strong>{voiceDrawState.phase === "ready" ? "VOICE READY" : voiceDrawState.message}</strong><span>{voiceRange ? `${voiceBeatLabel} · ${voiceRange.durationBeats.toFixed(2)}拍` : "1本指=VOICE · 2本指=PAN（VOICEは取消）"}</span><small>{voiceDrawState.phase === "ready" ? "指が画面に触れている間だけPitchを取得" : `${voiceDrawState.validFrameCount} frames · ${voiceDrawState.stableDurationMs}ms`}</small></>
            : selectedNoteIndex === null || !notes[selectedNoteIndex]
              ? <><strong>SELECT</strong><span>ノートをタップして選択</span><small>中央=移動 · 両端=長さ · 2本指=PAN</small></>
              : <><strong>{isUnresolvedMidiNote(notes[selectedNoteIndex]) ? "? · 音程未確定" : midiToNoteName(notes[selectedNoteIndex].midi)}</strong><span>開始 {notes[selectedNoteIndex].startBeat.toFixed(2)} · 長さ {notes[selectedNoteIndex].durationBeats.toFixed(2)}拍</span><small>中央=移動 · 両端=長さ · 2本指=PAN · SOURCE {notes[selectedNoteIndex].source ?? "AUTO"} · RAW {notes[selectedNoteIndex].rawStartTick ?? "—"} · REFINED {notes[selectedNoteIndex].refinedStartTick ?? "—"} · {notes[selectedNoteIndex].refinement ?? "NONE"}</small></>}
      </div>
    </section>
  );
}
